import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ConfirmationService, FilterMetadata } from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { isNullOrEmpty } from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { DataTableInput } from 'src/app/model/admin/data-table-input';
import { JobRole, JobRoleSortOrQuery } from 'src/app/model/admin/job-role';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { JobRoleDataTableInput } from 'src/app/model/admin/job-role-data-table-input';
import { getJobRoleColumns } from '../../definitions/job-role-table-definition';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { JobRoleTableService } from 'src/app/services/table/job-roles-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { CheckboxChangeEvent, Checkbox } from '@openng/optimus-ui/checkbox';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { MultiSelect } from '@openng/optimus-ui/multiselect';

@Component({
  selector: 'app-job-roles-container',
  templateUrl: './job-roles-container.component.html',
  styleUrls: ['./job-roles-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    MultiSelect,
    Checkbox,
    TranslatePipe,
  ],
})
export class JobRolesContainerComponent
  extends SearchContainerComponent<JobRole>
  implements OnInit, OnDestroy
{
  protected _contactApi = inject(ContactApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _jobRoleTableService = inject(JobRoleTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _confirmationService = inject(ConfirmationService);
  protected _authApi = inject(AuthApiService);
  protected _shiftTurnoverApi = inject(ShiftTurnoverApiService);

  canCreate = false;
  canEdit = false;
  canDelete = false;
  saving = false;
  jobRoleForm!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;
  filteredContacts: any[] = [];

  jobRoles: JobRole[] = [];
  selectedRole?: JobRole;
  showSuccessMessage = true;
  shiftGroups: { label: string; value: string }[] = [];
  selectedShiftGroups: string[] = [];

  activeTabChangedSub!: Subscription;

  ngOnInit() {
    this._jobRoleTableService.setLastLazyLoad(null);
    this.setTableService(this._jobRoleTableService);
    this.setupActiveTabChangedSubscription();
    this.loadShiftGroups();
    this.jobRoleForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      shiftGroup: [null],
      enabled: [{ value: false, disabled: true }],
      version: [null],
    });

    const screenName$ = this._translateService.get('CONTACT.SCREEN.JOB_ROLES');
    const dialogHeader$ = this._translateService.get(
      'CONTACT.LABEL.JOB_ROLE_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.JOB_ROLES.CREATE_JOB_ROLES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.JOB_ROLES.EDIT_JOB_ROLES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.JOB_ROLES.DELETE_JOB_ROLES,
    ]);

    this.jobRoleForm.disable();
    this.applyPreviousData(); // check into what these do
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._jobRoleTableService.setLastLazyLoad($event.lazy);
    const query: any = {
      name: null,
      shiftGroupsStr: null,
      enabled: null,
    };

    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (filters.shiftGroupsStr) {
      query.shiftGroupsStr = (filters.shiftGroupsStr as FilterMetadata)?.value;
    }
    if (
      filters.enabled &&
      !isNullOrUndefined((filters.enabled as FilterMetadata).value)
    ) {
      if ((filters.enabled as FilterMetadata).value) {
        query.enabled = 'true';
      } else {
        query.enabled = 'false';
      }
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._contactApi
      .queryForJobRoles(
        req.pageSize,
        req.page,
        query as JobRoleSortOrQuery,
        sort
      )
      .subscribe({
        next: (connection) => {
          this.jobRoles = connection.items;
          this.totalRecords = connection.totalRecords;
          this.elements = [...this.jobRoles];
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CONTACT.MESSAGES.ERROR.QUERY_JOB_ROLES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.loading = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'CONTACT.MESSAGES.SUCCESS.QUERY_JOB_ROLES',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  changeViewEvent($event: { data: JobRole }): void {
    this.selectedRole = $event.data;
    this.jobRoleForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      shiftGroup: $event.data.shiftGroups.map((sg) => {
        return {
          label: sg.name,
          value: sg.id,
        };
      }),
      enabled: $event.data.enabled,
      version: $event.data.version,
    });
    this.selectedShiftGroups = $event.data.shiftGroups.map((sg) => sg.id);

    if (this.canEdit) {
      this.jobRoleForm.enable();
    } else {
      this.jobRoleForm.disable();
    }
    this.displayDialog = true;
  }

  clearForm(): void {
    this.jobRoleForm.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedRole = undefined;
    this.selectedShiftGroups = [];
  }

  newJobRole(): void {
    this.jobRoleForm.reset();
    this.jobRoleForm.controls['enabled'].patchValue(false, { emitEvent: true });
    if (this.canCreate) {
      this.jobRoleForm.enable();
    } else {
      this.jobRoleForm.disable();
    }
    this.displayDialog = true;
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._jobRoleTableService.getLastLazyLoad()! });
  }

  nameChanged(): void {
    if (this.selectedRole) {
      this.selectedRole.name = this.jobRoleForm.controls['name'].value;
    }
  }

  toggleRecord($event: CheckboxChangeEvent): void {
    if (this.selectedRole) {
      this.selectedRole.enabled = $event.checked;
    }
  }

  shiftGroupChanged() {
    this.selectedShiftGroups = [];
    if (!isNullOrUndefined(this.selectedRole)) {
      this.jobRoleForm.controls['shiftGroup'].value.forEach((sg: any) => {
        this.selectedShiftGroups.push(sg.value);
      });
    }
  }

  saveJobRole(): void {
    if (!this.canCreate && !this.canEdit) {
      return;
    }

    if (!isNullOrUndefined(this.jobRoleForm) && this.jobRoleForm.valid) {
      let jobRole: JobRole | null = null;
      if (this.selectedRole) {
        jobRole = this.selectedRole;
      } else {
        jobRole = {
          id: '',
          name: this.jobRoleForm.controls['name'].value,
          enabled: this.jobRoleForm.controls['enabled'].value,
          version: this.jobRoleForm.controls['version'].value,
          shiftGroups: [],
          shiftGroupsStr: '',
        };
      }
      if (!jobRole.id) {
        this.createJobRole(jobRole);
      } else {
        this.updateJobRole(jobRole, true);
      }
    }
  }

  createJobRole(jobRole: JobRole) {
    if (!this.canCreate) {
      return;
    }

    if (!isNullOrEmpty(this.jobRoleForm?.controls['shiftGroup']?.value)) {
      this.jobRoleForm?.controls['shiftGroup']?.value.forEach((sg: any) => {
        this.selectedShiftGroups.push(sg.value);
      });
    }

    const jobRoleInput: DataTableInput | null =
      this._adminBuilder.buildJobRoleDataTableInput(
        jobRole,
        true,
        this.selectedShiftGroups
      );

    this.saving = true;
    this._contactApi.createJobRole(jobRoleInput).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CONTACT.MESSAGES.ERROR.CREATE_JOB_ROLE',
          bodyParams: { name: jobRole.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CONTACT.MESSAGES.SUCCESS.CREATE_JOB_ROLE',
          bodyParams: { name: jobRole.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.refresh();
      },
    });
  }

  updateJobRole(changes: JobRole, closeDialog: boolean) {
    if (!this.canEdit) {
      return;
    }

    const jobRoleChanges: JobRoleDataTableInput | null =
      this._adminBuilder.buildJobRoleDataTableInput(
        changes,
        false,
        this.selectedShiftGroups
      );

    this.saving = true;
    this._contactApi.updateJobRole(changes.id, jobRoleChanges).subscribe({
      next: (jobRole) => {
        if (!closeDialog && !isNullOrUndefined(jobRole)) {
          this.changeViewEvent({ data: jobRole });
        } else {
          this.clearForm();
          this.displayDialog = false;
          this.refresh();
        }
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CONTACT.MESSAGES.ERROR.UPDATE_JOB_ROLE',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CONTACT.MESSAGES.SUCCESS.UPDATE_JOB_ROLE',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
      },
    });
  }

  deleteJobRole(): void {
    if (!this.canDelete) {
      return;
    }

    if (!isNullOrUndefined(this.jobRoleForm)) {
      const jobRole = Object.assign({}, this.jobRoleForm.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'CONTACT.MESSAGES.HEADERS.DELETE_JOB_ROLE'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'CONTACT.MESSAGES.CONFIRMATION.DELETE_JOB_ROLE',
        { name: jobRole.name }
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(jobRole.id)) {
            // Existing call type so delete it
            this._contactApi
              .allowJobRoleDelete(jobRole.id)
              .subscribe((response) => {
                if (!response) {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey:
                      'Delete cannot be performed, the entry is already associated with records.',
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                  });
                } else {
                  this._contactApi.deleteJobRole(jobRole.id).subscribe({
                    next: (result) => {
                      this.saving = false;
                      this.clearForm();
                      this.displayDialog = false;
                    },
                    error: (error) => {
                      this._logAndMessage.errorLogOnly(error);
                      this._logAndMessage.translateToErrorAlert({
                        bodyKey: 'CONTACT.MESSAGES.ERROR.DELETE_JOB_ROLE',
                        bodyParams: { name: jobRole.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                      });
                    },
                    complete: () => {
                      this._logAndMessage.translateToSuccessMessage({
                        bodyKey: 'CONTACT.MESSAGES.SUCCESS.DELETE_JOB_ROLE',
                        bodyParams: { name: jobRole.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                      });
                      this.refresh();
                    },
                  });
                }
              });
          } else {
            // new call type so just close dialog
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  loadShiftGroups() {
    this._shiftTurnoverApi.getShiftGroups().subscribe((groups) => {
      this.shiftGroups = [
        ...groups.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ].sort((a, b) => sortByProperty(a, b, 'label'));
      this.columns = getJobRoleColumns(this.shiftGroups);
    });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
